import * as entityActions from 'client/actions/entityActions'
import LibraryElementEnum from 'client/enums/LibraryElementEnum'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import useCurrentRootEntity from 'client/hooks/useCurrentRootEntity'
import { createPage } from 'client/pages'
import { pageSelectors, typedPageSelectors, usePage } from 'client/store/index'
import { needScroll } from 'client/store/management/managementActions'
import {
  addStructure,
  toggleInstantUpload,
  toggleSettings,
} from 'client/store/page/pageActions'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { useActions } from 'common/hooks/useActions'
import { findEntityByType } from 'common/utils/entityUtils'
import React from 'react'
import { useDrag } from 'react-dnd'
import { twMerge } from 'tailwind-merge'

type LibraryElementProps = {
  type: string
  title: string
  className?: string
  iconRenderer?: () => React.ReactNode
}

export const LibraryElement = ({
  type,
  className,
  title,
  iconRenderer,
}: LibraryElementProps) => {
  const dispatch = useAppDispatch()
  const createAsLast = useActions(entityActions.createAsLast)
  const [{ isDragging }, drag] = useDrag({
    item: { type },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const rootEntity = useCurrentRootEntity()
  const pageLocale = usePage(pageSelectors.getPageLocale) as string
  const pageType = usePage(typedPageSelectors.getPageType)

  function appendLibraryElement() {
    if (
      type in LibraryElementEnum &&
      type !== LibraryElementEnum.LibrarySection
    ) {
      const { createLibraryElementStructure } = createPage(pageType)
      const structure = createLibraryElementStructure({
        libraryElementType: type as LibraryElementEnum,
        container: rootEntity,
        locale: pageLocale,
        position: rootEntity.childIds.length,
      })
      dispatch(addStructure(structure))
      dispatch(needScroll(true))
      if (type === LibraryElementEnum.LibraryCalendar) {
        const calendar = findEntityByType(
          structure,
          EntityTypeEnum.BookingCalendar,
        )
        if (calendar) {
          dispatch(toggleSettings(calendar.id))
        }
      }
      if (type === LibraryElementEnum.LibraryImage) {
        dispatch(toggleInstantUpload())
        const image = findEntityByType(structure, EntityTypeEnum.Image)
        if (image) {
          dispatch(toggleSettings(image.id))
        }
      }
    } else {
      createAsLast(type)
    }
  }

  return (
    <button
      className={twMerge(
        'h-[105px] rounded-md bg-gray-50',
        'cursor-move select-none text-center transition-[opacity_0.25s_ease-in]',
        '[word-break:break-word] hover:bg-black/3',
        isDragging && 'bg-black/3',
      )}
      ref={drag}
      type="button"
      onClick={appendLibraryElement}
    >
      <div className="flex h-full w-full flex-col items-center justify-evenly p-1.5">
        <span
          className={twMerge(
            'pointer-events-none flex h-9 w-9 basis-[40%] items-center justify-center',
            'rounded-sm bg-black/5 p-2',
          )}
        >
          {className && (
            <div className={twMerge('h-[15px] text-M', className)} />
          )}
          {iconRenderer?.()}
        </span>
        <span
          className={twMerge(
            'pointer-events-none line-clamp-2 text-M',
            isDragging && 'opacity-0',
          )}
          title={title}
        >
          {title}
        </span>
      </div>
    </button>
  )
}
